import { MessageTemplatesCard } from '../components/MessageTemplatesCard';
import { WEDDING_TEMPLATES } from '../lib/messageTemplates';
import { RUNTIME_BACKEND_URL } from '../lib/backendUrl';
import { useState, useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import { GoogleLinkCard } from '../components/auth/GoogleLinkCard';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { formatDate } from '../lib/utils';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Sun, Moon, User, Building, Mail, Phone, Palette, Bell, Shield, Download,
  CreditCard, Calendar, CheckCircle, Edit, Loader2, Lock, Eye, EyeOff,
  FileSpreadsheet, FileText, Plus, Trash2, Star, Copy, Info, Crown
} from 'lucide-react';

const API = RUNTIME_BACKEND_URL;

const placeholders = [
  { key: '{bride_name}', description: 'Gelin adi' },
  { key: '{groom_name}', description: 'Damat adi' },
  { key: '{wedding_date}', description: 'Dugun tarihi' },
  { key: '{services}', description: 'Secilen hizmetler' },
  { key: '{total_price}', description: 'Toplam tutar' },
  { key: '{deposit_amount}', description: 'Kapora tutari' },
  { key: '{remaining_amount}', description: 'Kalan tutar' },
];

export default function SettingsPage() {
  const { user, updateTheme, updateUser, token } = useAuthStore();
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Plan info
  const [planInfo, setPlanInfo] = useState(null);

  // User templates
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateForm, setTemplateForm] = useState({ name: '', content: '', is_default: false });

  const [profileForm, setProfileForm] = useState({
    full_name: user?.full_name || '',
    company_name: user?.company_name || '',
    phone: user?.phone || '',
    address: user?.address || '',
    city: user?.city || '',
    district: user?.district || '',
    postal_code: user?.postal_code || ''
  });

  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  // Fetch plan info and templates on mount
  useEffect(() => {
    if (token && user?.role !== 'admin') {
      fetchPlanInfo();
      fetchTemplates();
    }
  }, [token]);

  const fetchPlanInfo = async () => {
    try {
      const res = await fetch(`${API}/api/my-plan`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setPlanInfo(await res.json());
    } catch (e) { /* ignore */ }
  };

  const fetchTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const res = await fetch(`${API}/api/my-templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setTemplates(await res.json());
    } catch (e) { /* ignore */ }
    finally { setLoadingTemplates(false); }
  };

  const handleThemeToggle = () => {
    const newTheme = user?.theme === 'dark' ? 'light' : 'dark';
    updateTheme(newTheme);
  };

  const openProfileDialog = () => {
    setProfileForm({
      full_name: user?.full_name || '',
      company_name: user?.company_name || '',
      phone: user?.phone || '',
      address: user?.address || '',
      city: user?.city || '',
      district: user?.district || '',
      postal_code: user?.postal_code || ''
    });
    setShowProfileDialog(true);
  };

  const handleSaveProfile = async () => {
    if (!profileForm.full_name.trim()) {
      toast.error('Ad Soyad zorunludur');
      return;
    }
    setSavingProfile(true);
    try {
      const res = await fetch(`${API}/api/auth/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(profileForm)
      });
      if (!res.ok) { const data = await res.json(); throw new Error(data.detail || 'Guncellenemedi'); }
      const data = await res.json();
      updateUser(data.user);
      toast.success('Profil guncellendi');
      setShowProfileDialog(false);
    } catch (error) { toast.error(error.message); }
    finally { setSavingProfile(false); }
  };

  const handleChangePassword = async () => {
    if (!passwordForm.current_password) { toast.error('Mevcut sifrenizi girin'); return; }
    if (passwordForm.new_password.length < 6) { toast.error('Yeni sifre en az 6 karakter olmali'); return; }
    if (passwordForm.new_password !== passwordForm.confirm_password) { toast.error('Yeni sifreler eslesmiyor'); return; }
    setSavingPassword(true);
    try {
      const res = await fetch(`${API}/api/auth/change-password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ current_password: passwordForm.current_password, new_password: passwordForm.new_password })
      });
      if (!res.ok) { const data = await res.json(); throw new Error(data.detail || 'Sifre degistirilemedi'); }
      toast.success('Sifre basariyla degistirildi');
      setShowPasswordDialog(false);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) { toast.error(error.message); }
    finally { setSavingPassword(false); }
  };

  // --- Template CRUD ---
  const openNewTemplate = () => {
    setEditingTemplate(null);
    setTemplateForm({ name: '', content: '', is_default: templates.length === 0 });
    setShowTemplateDialog(true);
  };

  const openEditTemplate = (t) => {
    setEditingTemplate(t);
    setTemplateForm({ name: t.name, content: t.content, is_default: t.is_default });
    setShowTemplateDialog(true);
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name.trim()) { toast.error('Sablon adi zorunlu'); return; }
    if (!templateForm.content.trim()) { toast.error('Sablon icerigi zorunlu'); return; }
    setSavingTemplate(true);
    try {
      const url = editingTemplate
        ? `${API}/api/my-templates/${editingTemplate.id}`
        : `${API}/api/my-templates`;
      const method = editingTemplate ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method, headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(templateForm)
      });
      if (!res.ok) throw new Error('Kaydedilemedi');
      toast.success(editingTemplate ? 'Sablon guncellendi' : 'Sablon olusturuldu');
      setShowTemplateDialog(false);
      fetchTemplates();
    } catch (e) { toast.error(e.message); }
    finally { setSavingTemplate(false); }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Bu sablonu silmek istediginize emin misiniz?')) return;
    try {
      const res = await fetch(`${API}/api/my-templates/${id}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Silinemedi');
      toast.success('Sablon silindi');
      fetchTemplates();
    } catch (e) { toast.error(e.message); }
  };

  const handleSetDefault = async (t) => {
    try {
      await fetch(`${API}/api/my-templates/${t.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ is_default: true })
      });
      toast.success(`"${t.name}" varsayilan yapildi`);
      fetchTemplates();
    } catch (e) { toast.error('Guncellenemedi'); }
  };

  const copyTemplateContent = (content) => {
    navigator.clipboard.writeText(content);
    toast.success('Sablon icerigi panoya kopyalandi');
  };

  // Plan price calculation
  const getPlanPrice = () => {
    if (planInfo?.user_price && planInfo.user_price > 0) {
      return planInfo.user_price;
    }
    if (planInfo?.plan) {
      if (planInfo.subscription_plan === 'monthly') return planInfo.plan.price_monthly;
      if (planInfo.subscription_plan === 'yearly') return planInfo.plan.price_yearly;
    }
    return 0;
  };

  const getPlanPriceLabel = () => {
    const price = getPlanPrice();
    if (!price) return '-';
    if (planInfo?.subscription_plan === 'yearly') return `${price.toLocaleString('tr-TR')} TL/yil`;
    return `${price.toLocaleString('tr-TR')} TL/ay`;
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold">Ayarlar</h1>
        <p className="text-muted-foreground mt-1">Hesap ve uygulama ayarlariniz</p>
      </div>

      <GoogleLinkCard apiBase="/api/auth" token={token} />

      <MessageTemplatesCard path="/api" token={token} defaults={WEDDING_TEMPLATES} />

      {/* Profile Info */}
      <Card className="animate-fade-in">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-primary" />
            Profil Bilgileri
          </CardTitle>
          <Button variant="outline" size="sm" onClick={openProfileDialog}>
            <Edit className="h-4 w-4 mr-1" /> Duzenle
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-muted-foreground">Ad Soyad</Label>
              <p className="font-medium mt-1">{user?.full_name}</p>
            </div>
            <div>
              <Label className="text-muted-foreground">E-posta</Label>
              <p className="font-medium mt-1 flex items-center gap-2">
                <Mail className="h-4 w-4" />{user?.email}
              </p>
            </div>
            {user?.phone && (
              <div>
                <Label className="text-muted-foreground">Telefon</Label>
                <p className="font-medium mt-1 flex items-center gap-2">
                  <Phone className="h-4 w-4" />{user.phone}
                </p>
              </div>
            )}
            {user?.company_name && (
              <div>
                <Label className="text-muted-foreground">Sirket / Studyo</Label>
                <p className="font-medium mt-1 flex items-center gap-2">
                  <Building className="h-4 w-4" />{user.company_name}
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Subscription Info */}
      {user?.role !== 'admin' && (
        <Card className="animate-fade-in stagger-1" data-testid="subscription-info-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-primary" />
              Abonelik Bilgileri
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-4 bg-muted/50 rounded-lg">
                <Label className="text-muted-foreground text-xs">Abonelik Durumu</Label>
                <p className="font-medium mt-1 flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  {user?.subscription_status === 'active' ? 'Aktif' :
                   user?.subscription_status === 'pending' ? 'Onay Bekliyor' : 'Suresi Dolmus'}
                </p>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg">
                <Label className="text-muted-foreground text-xs">Paket</Label>
                <p className="font-medium mt-1">
                  {planInfo?.plan?.name || (user?.subscription_plan === 'monthly' ? 'Aylik Paket' :
                   user?.subscription_plan === 'yearly' ? 'Yillik Paket' : 'Belirsiz')}
                </p>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg">
                <Label className="text-muted-foreground text-xs">Baslangic Tarihi</Label>
                <p className="font-medium mt-1 flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  {user?.subscription_started_at
                    ? formatDate(user.subscription_started_at.split('T')[0])
                    : '-'}
                </p>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg">
                <Label className="text-muted-foreground text-xs">Ucret</Label>
                <p className="font-medium mt-1 text-primary text-lg" data-testid="plan-price">
                  {getPlanPriceLabel()}
                </p>
              </div>
            </div>
            {planInfo?.plan?.features && (
              <div className="text-xs text-muted-foreground space-y-0.5">
                {planInfo.plan.features.map((f, i) => (
                  <span key={i} className="inline-flex items-center gap-1 mr-3">
                    <CheckCircle className="h-3 w-3 text-green-500" />{f}
                  </span>
                ))}
              </div>
            )}
            {user?.subscription_expires_at && (
              <div className="text-sm text-muted-foreground">
                Bitis Tarihi: <strong>{formatDate(user.subscription_expires_at.split('T')[0])}</strong>
              </div>
            )}
            <div className="pt-2 border-t flex flex-wrap gap-2">
              <Button
                onClick={() => window.location.href = '/panel/abonelik'}
                size="sm"
                className="bg-primary hover:bg-primary/90"
                data-testid="upgrade-subscription-btn">
                <Crown className="h-4 w-4 mr-2" />
                Planı Yükselt / Yenile
              </Button>
              <p className="text-xs text-muted-foreground self-center">
                Aboneliğinizi yönetmek, yenilemek veya farklı bir plana geçmek için tıklayın.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Agreement Templates */}
      {user?.role !== 'admin' && (
        <Card className="animate-fade-in stagger-2" data-testid="agreement-templates-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Anlasma Sablonlari
            </CardTitle>
            <Button size="sm" onClick={openNewTemplate} data-testid="add-template-btn">
              <Plus className="h-4 w-4 mr-1" /> Yeni Sablon
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Musterilerinize gondereceyiniz anlasma mesajlarinin sablonlarini buradan yonetin.
              Varsayilan sablon, musteri detay sayfasindan "Anlasma Gonder" tiklandiginda kullanilir.
            </p>

            {loadingTemplates ? (
              <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
            ) : templates.length === 0 ? (
              <div className="text-center py-6 text-muted-foreground text-sm">
                <FileText className="h-8 w-8 mx-auto mb-2 opacity-30" />
                <p>Henuz sablon olusturmadiniz.</p>
                <p className="text-xs mt-1">Yeni sablon ekleyerek baslayabilirsiniz.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {templates.map(t => (
                  <div key={t.id} className={`border rounded-lg p-3 ${t.is_default ? 'border-primary bg-primary/5' : ''}`}
                    data-testid={`template-${t.id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{t.name}</span>
                        {t.is_default && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-medium flex items-center gap-0.5">
                            <Star className="h-2.5 w-2.5" /> Varsayilan
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                          onClick={() => copyTemplateContent(t.content)} title="Kopyala">
                          <Copy className="h-3.5 w-3.5" />
                        </Button>
                        {!t.is_default && (
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                            onClick={() => handleSetDefault(t)} title="Varsayilan Yap">
                            <Star className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                          onClick={() => openEditTemplate(t)} title="Duzenle">
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-600"
                          onClick={() => handleDeleteTemplate(t.id)} title="Sil">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-2 whitespace-pre-line">{t.content}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Theme Settings */}
      <Card className="animate-fade-in stagger-3">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="h-5 w-5 text-primary" />
            Gorunum
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {user?.theme === 'dark' ? <Moon className="h-5 w-5 text-primary" /> : <Sun className="h-5 w-5 text-primary" />}
              <div>
                <p className="font-medium">Tema</p>
                <p className="text-sm text-muted-foreground">
                  {user?.theme === 'dark' ? 'Koyu tema aktif' : 'Acik tema aktif'}
                </p>
              </div>
            </div>
            <Switch checked={user?.theme === 'dark'} onCheckedChange={handleThemeToggle} data-testid="theme-switch" />
          </div>
        </CardContent>
      </Card>

      {/* Security */}
      <Card className="animate-fade-in stagger-4">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            Guvenlik
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Button variant="outline" onClick={() => setShowPasswordDialog(true)}>
            <Lock className="h-4 w-4 mr-2" />Sifre Degistir
          </Button>
        </CardContent>
      </Card>

      {/* Notification Settings */}
      <Card className="animate-fade-in stagger-5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-primary" />
            Bildirim Ayarlari
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">SMS Bildirimleri</p>
              <p className="text-sm text-muted-foreground">Twilio API gerekli</p>
            </div>
            <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
              Yapilandirilmadi
            </span>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">WhatsApp Bildirimleri</p>
              <p className="text-sm text-muted-foreground">Twilio API gerekli</p>
            </div>
            <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
              Yapilandirilmadi
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Export */}
      <Card className="animate-fade-in stagger-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-primary" />
            Veri Yedekleme
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Tum musteri kayitlarinizi ve anlasma bilgilerinizi Excel formatinda indirin.
          </p>
          <Button variant="outline" disabled={isExporting}
            onClick={async () => {
              setIsExporting(true);
              try {
                const response = await fetch(`${API}/api/export/customers/excel`, {
                  headers: { 'Authorization': `Bearer ${token}` }
                });
                if (!response.ok) throw new Error('Yedekleme basarisiz');
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `musteri_yedek_${new Date().toISOString().split('T')[0]}.xlsx`;
                document.body.appendChild(a); a.click(); document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
                toast.success('Yedekleme dosyasi indirildi');
              } catch (error) { toast.error(error.message); }
              finally { setIsExporting(false); }
            }}>
            {isExporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
            Excel Olarak Indir
          </Button>
        </CardContent>
      </Card>

      {/* Profile Dialog */}
      <Dialog open={showProfileDialog} onOpenChange={setShowProfileDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><User className="h-5 w-5" />Profil Duzenle</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Ad Soyad *</Label>
              <Input value={profileForm.full_name} onChange={(e) => setProfileForm({...profileForm, full_name: e.target.value})} placeholder="Ad Soyad" />
            </div>
            <div className="space-y-2">
              <Label>Sirket / Studyo Adi</Label>
              <Input value={profileForm.company_name} onChange={(e) => setProfileForm({...profileForm, company_name: e.target.value})} placeholder="Sirket adi" />
            </div>
            <div className="space-y-2">
              <Label>Telefon</Label>
              <Input value={profileForm.phone} onChange={(e) => setProfileForm({...profileForm, phone: e.target.value})} placeholder="05XX XXX XX XX" />
            </div>

            {/* Kargo / Sevkiyat Adresi — albümcüler basili albümleri buraya kargolayacak */}
            <div className="border-t pt-3 mt-1">
              <p className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1">
                <Building className="h-3.5 w-3.5" />
                Kargo / Sevkiyat Adresi
                <span className="text-[10px] text-muted-foreground/70 font-normal">(albümcü basılı albümü buraya gönderir)</span>
              </p>
              <div className="space-y-2">
                <Textarea
                  value={profileForm.address || ''}
                  onChange={(e) => setProfileForm({...profileForm, address: e.target.value})}
                  placeholder="Açık adres (sokak, mahalle, bina no, daire no...)"
                  rows={2}
                  data-testid="profile-address-input"
                />
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    value={profileForm.district || ''}
                    onChange={(e) => setProfileForm({...profileForm, district: e.target.value})}
                    placeholder="İlçe"
                    data-testid="profile-district-input"
                  />
                  <Input
                    value={profileForm.city || ''}
                    onChange={(e) => setProfileForm({...profileForm, city: e.target.value})}
                    placeholder="İl"
                    data-testid="profile-city-input"
                  />
                </div>
                <Input
                  value={profileForm.postal_code || ''}
                  onChange={(e) => setProfileForm({...profileForm, postal_code: e.target.value})}
                  placeholder="Posta kodu (opsiyonel)"
                  data-testid="profile-postal-input"
                />
              </div>
            </div>

            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setShowProfileDialog(false)}>Iptal</Button>
              <Button onClick={handleSaveProfile} disabled={savingProfile}>
                {savingProfile && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Kaydet
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Lock className="h-5 w-5" />Sifre Degistir</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Mevcut Sifre *</Label>
              <div className="relative">
                <Input type={showCurrentPassword ? "text" : "password"} value={passwordForm.current_password}
                  onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})} placeholder="Mevcut sifreniz" />
                <Button type="button" variant="ghost" size="icon" className="absolute right-0 top-0 h-full"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}>
                  {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Yeni Sifre *</Label>
              <div className="relative">
                <Input type={showNewPassword ? "text" : "password"} value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})} placeholder="En az 6 karakter" />
                <Button type="button" variant="ghost" size="icon" className="absolute right-0 top-0 h-full"
                  onClick={() => setShowNewPassword(!showNewPassword)}>
                  {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Yeni Sifre (Tekrar) *</Label>
              <Input type="password" value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})} placeholder="Yeni sifreyi tekrar girin" />
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setShowPasswordDialog(false)}>Iptal</Button>
              <Button onClick={handleChangePassword} disabled={savingPassword}>
                {savingPassword && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Sifreyi Degistir
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Template Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {editingTemplate ? 'Sablonu Duzenle' : 'Yeni Anlasma Sablonu'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Sablon Adi *</Label>
              <Input value={templateForm.name} onChange={(e) => setTemplateForm({...templateForm, name: e.target.value})}
                placeholder="Ornek: Standart Anlasma" data-testid="template-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Sablon Icerigi *</Label>
              <Textarea value={templateForm.content} onChange={(e) => setTemplateForm({...templateForm, content: e.target.value})}
                placeholder="Anlasma metnini buraya yazin..." rows={10} className="font-mono text-sm" data-testid="template-content-input" />
            </div>
            <div className="bg-muted/50 rounded-lg p-3">
              <p className="text-xs font-medium mb-2 flex items-center gap-1">
                <Info className="h-3 w-3" /> Kullanilabilir degiskenler (otomatik doldurulur):
              </p>
              <div className="flex flex-wrap gap-1.5">
                {placeholders.map(p => (
                  <button key={p.key}
                    className="text-[11px] px-2 py-0.5 rounded bg-background border hover:bg-primary/10 hover:border-primary/30 transition-colors"
                    onClick={() => setTemplateForm({...templateForm, content: templateForm.content + p.key})}
                    title={p.description}>
                    {p.key}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={templateForm.is_default}
                onCheckedChange={(v) => setTemplateForm({...templateForm, is_default: v})} />
              <Label className="text-sm">Varsayilan sablon olarak ayarla</Label>
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setShowTemplateDialog(false)}>Iptal</Button>
              <Button onClick={handleSaveTemplate} disabled={savingTemplate} data-testid="save-template-btn">
                {savingTemplate && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {editingTemplate ? 'Guncelle' : 'Olustur'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
